/* Messages — a church admin writes once and the congregation hears it.
 *
 * The client's scope: "church bulk messaging via in-app, push and SMS", to an
 * audience chosen "by user type per church group".
 *
 * COMPOSER AND HISTORY ON ONE SCREEN. Somebody who has just sent an
 * announcement to three thousand people wants to watch it land — splitting
 * that across two pages means writing, submitting, then going to look for it.
 * The history sits under the composer and the newest row updates itself while
 * the send is in flight.
 *
 * WEB FIRST, and possibly web only: this is a desk task. Writing two hundred
 * words to a congregation is not something anybody does standing at a bus
 * stop, and the mobile app is where people RECEIVE these.
 */
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { ChosenPeople } from './ArrangeDialog'
import { DevotionsPanel } from './DevotionsPanel'
import {
  AttachButtons,
  AttachedFiles,
  AttachmentList,
  useAttachments,
} from './AttachmentPicker'
import { TemplatePicker, type TemplateChoice } from './TemplatePicker'
import { useTabParam } from '@/lib/useTabParam'
import {
  AlertCircle,
  Bell,
  Check,
  Loader2,
  MessageSquare,
  Send,
  Smartphone,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field, SelectField, TextArea } from '@/components/ui/field'
import {
  useFeatures,
  useGroups,
  useMe,
  useMessage,
  useMessages,
  useMessagingAnalytics,
  useMessageTemplates,
  useMyPermissions,
  useRoles,
  useSendMessage,
} from '@/api/queries'
import { cn, timeAgo } from '@/lib/utils'
import type { AudienceFilter, AudienceKind, ChurchMessage } from '@/types/api'
import {
  AudienceCount,
  EMPTY_FILTER,
  RuleBuilder,
  completeFilter,
} from './audience/RuleBuilder'

const MAX_TITLE = 120
const MAX_BODY = 2000

export function MessagesPage() {
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null

  /* The same question the server asks. A moderator can be given
     messages.bulk without being made an administrator, so the account role
     alone is the wrong gate — it was already wrong once on the sidebar. */
  const permissions = useMyPermissions(churchId)
  const maySend =
    me?.role === 'church_admin' ||
    me?.role === 'super_admin' ||
    (permissions.data?.includes('messages.bulk') ?? false)

  const messages = useMessages(churchId)
  const [tab, setTab] = useTabParam(['write', 'devotions'] as const, 'write')

  /* People chosen on another screen — the absentees from a service day —
     arrive in the navigation state and become the audience. Cleared once
     sent, so a reload does not write to them twice. */
  const location = useLocation()
  const navigate = useNavigate()
  /* Writing to chosen people, and writing to the church or its groups, are
     each the church's to offer. Daily devotions go to everyone, so they
     follow the second. */
  const features = useFeatures()
  const bulk = features.send_bulk_msgs
  const chosen = (location.state as { people?: ChosenPeople } | null)?.people ?? null
  const people = features.send_individual_messages ? chosen : null
  function clearPeople() {
    navigate(location.pathname + location.search, { replace: true, state: null })
  }

  /* The message currently being sent, polled until it settles.
     In an effect, not during render: setting state while rendering is how a
     component ends up re-rendering itself forever. */
  const [watching, setWatching] = useState<string | null>(null)
  const live = useMessage(churchId, watching, { poll: Boolean(watching) })

  const liveStatus = live.data?.status
  /* Waiting on SMS is a settled state, not work in progress — the campaign
     engine may not run for hours, and polling until then would spin all
     afternoon. */
  const liveWaiting = Boolean(live.data?.waiting_on)
  useEffect(() => {
    if (!watching) return
    if (liveStatus === 'sent' || liveStatus === 'failed' || liveWaiting) {
      setWatching(null)
      /* The list still holds the row as it was when it was queued; the final
         counts live on the row we have just stopped polling. */
      messages.refetch()
    }
  }, [watching, liveStatus, liveWaiting, messages])

  if (!churchId) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        Messages are for a church. Join one to write to its members.
      </Card>
    )
  }

  if (!features.anyMessaging) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        Messages aren't available for your church.
      </Card>
    )
  }

  if (permissions.isLoading) {
    return (
      <Card className="p-s6 text-center text-ink-2">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
      </Card>
    )
  }

  if (!maySend) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        Writing to the whole church is for administrators. Ask yours if
        something needs announcing.
      </Card>
    )
  }

  const rows = messages.data?.items ?? []

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-lg font-extrabold tracking-tight">Messages</h1>
        <p className="text-base text-ink-2">
          Write to your church. It arrives in their notifications and on their
          phones.
        </p>
      </header>

      <Reach churchId={churchId} />

      {/* Devotions are the same machinery on a schedule — the client lists
          them inside bulk messaging, so they are a tab here rather than a
          page of their own. */}
      {bulk ? (
        <div className="flex gap-s1 border-b border-line-2">
          <Tab active={tab === 'write'} onClick={() => setTab('write')}>
            Write
          </Tab>
          <Tab active={tab === 'devotions'} onClick={() => setTab('devotions')}>
            Daily devotions
          </Tab>
        </div>
      ) : null}

      {tab === 'devotions' && bulk ? (
        <DevotionsPanel churchId={churchId} />
      ) : (
        <>
          {bulk || people ? (
            <Composer
              key={people ? people.ids.join(',') : 'everyone'}
              churchId={churchId}
              people={people}
              bulk={bulk}
              onSent={(id) => {
                setWatching(id)
                messages.refetch()
                if (people) clearPeople()
              }}
            />
          ) : (
            /* Only messages to chosen people are offered, and none have
               been chosen — they are picked on a service day's attendance. */
            <Card className="p-s6 text-center text-base text-ink-2">
              Messages to the whole church or its groups aren't available for
              your church. To write to particular people, choose them from a
              service day's attendance.
            </Card>
          )}

          <History
            rows={rows}
            live={watching ? live.data ?? null : null}
            loading={messages.isLoading}
          />
        </>
      )}
    </div>
  )
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        '-mb-px border-b-2 px-s3 py-s2 text-md font-semibold transition-colors',
        active
          ? 'border-brand text-brand'
          : 'border-transparent text-ink-2 hover:text-ink',
      )}
    >
      {children}
    </button>
  )
}

/* --------------------------------------------------------------------- reach
 *
 * HOW MANY OF THE CONGREGATION COULD BE REACHED AT ALL. Not how one message
 * did — that lives on its report. This is the number an administrator can
 * act on: if half the church has never opened the app, that is something to
 * say from the front on Sunday, and no per-message report reveals it.
 */

function Reach({ churchId }: { churchId: string }) {
  const stats = useMessagingAnalytics(churchId)
  if (!stats.data) return null
  const s = stats.data

  return (
    <Card className="flex flex-wrap items-center gap-x-s5 gap-y-s2 p-s4">
      <Stat value={s.members} label="members" />
      <Stat
        value={s.push_reachable}
        label="can get push"
        /* Amber when most of the church is unreachable on their phones —
           the one number here worth acting on. */
        warn={s.members > 0 && s.push_reachable * 2 < s.members}
      />
      <Stat value={s.sms_reachable} label="have a mobile number" />
      {s.bell_entries > 0 ? (
        <Stat value={`${s.read_rate}%`} label="open what you send" />
      ) : null}
    </Card>
  )
}

function Stat({
  value,
  label,
  warn,
}: {
  value: number | string
  label: string
  warn?: boolean
}) {
  return (
    <div>
      <div
        className={cn(
          'text-lg font-extrabold tracking-tight',
          warn && 'text-warning-ink',
        )}
      >
        {value}
      </div>
      <div className="text-sm text-ink-2">{label}</div>
    </div>
  )
}

/* ------------------------------------------------------------------ composer */

function Composer({
  churchId,
  people,
  bulk,
  onSent,
}: {
  churchId: string
  /** A list chosen elsewhere; offered as the first audience. */
  people?: ChosenPeople | null
  /** Whether the church, a group, a role or rules may be written to. */
  bulk: boolean
  onSent: (messageId: string) => void
}) {
  const groups = useGroups(churchId)
  const roles = useRoles(churchId)
  const send = useSendMessage(churchId)
  const attachments = useAttachments()

  /* Texts are only possible once at least one sentence has been approved
     for them. Asked of the server rather than assumed, so the day an
     approval lands the switch works without anybody shipping anything. */
  const smsTemplates = useMessageTemplates('sms')
  const canSendTexts = (smsTemplates.data ?? []).some((t) => t.is_registered)

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState<AudienceKind>(people ? 'list' : 'church')
  const [groupId, setGroupId] = useState('')
  const [roleId, setRoleId] = useState('')
  const [rules, setRules] = useState<AudienceFilter>(EMPTY_FILTER)
  /* SMS is off by design. It costs money per recipient and needs DLT
     registration, so it is opted into rather than out of. */
  const [channels, setChannels] = useState({
    in_app: true,
    push: true,
    sms: false,
  })
  /* Only set when a regulated channel is ticked. See TemplatePicker: those
     channels cannot take free text at all. */
  const [choice, setChoice] = useState<TemplateChoice | null>(null)
  const [error, setError] = useState<string | null>(null)

  /* TEMPLATE-FIRST WHEN A REGULATED CHANNEL IS TICKED, free text otherwise.
     Keeping the everyday path simple and making the regulated one obviously
     different beats one composer that quietly behaves two ways. */
  const regulated = channels.sms

  const needsGroup = audience === 'group' || audience === 'group_role'
  const byRules = audience === 'filter'
  const chosenRules = byRules ? completeFilter(rules) : null
  const ready =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (!needsGroup || groupId) &&
    (!byRules || chosenRules !== null) &&
    (audience !== 'group_role' || roleId) &&
    (audience !== 'list' || (people?.ids.length ?? 0) > 0) &&
    (channels.in_app || channels.push || channels.sms) &&
    /* A half-filled template would go out as a sentence with a gap in it. */
    (!regulated || choice !== null) &&
    /* Sending mid-upload would drop the file silently. */
    !attachments.isUploading

  function submit() {
    if (!ready || send.isPending) return
    setError(null)
    send.mutate(
      {
        title: title.trim(),
        body: body.trim(),
        audience_kind: audience,
        // Rules may be narrowed to one group; empty means the church.
        group_id: needsGroup || byRules ? groupId || null : null,
        role_id: audience === 'group_role' ? roleId : null,
        user_ids: audience === 'list' ? people?.ids : undefined,
        audience_filter: chosenRules,
        channels,
        media_ids: attachments.mediaIds,
      },
      {
        onSuccess: (message) => {
          setTitle('')
          setBody('')
          attachments.reset()
          setChoice(null)
          onSent(message.id)
        },
        onError: (err) => setError((err as Error).message),
      },
    )
  }

  return (
    <Card className="space-y-s4 p-s4">
      <Field
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
        placeholder="Sunday service moves to 9:30"
        required
      />

      <TextArea
        label="Message"
        value={body}
        onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
        placeholder="Write what your church needs to know."
        rows={4}
        required
        /* The count only appears near the limit. A counter on an empty box
           makes a short announcement feel like homework. */
        help={
          regulated
            ? 'This is what appears in the app and on their phones. Texts use the approved wording below.'
            : body.length > MAX_BODY - 200
              ? `${MAX_BODY - body.length} characters left`
              : undefined
        }
      />

      {/* FILES. Below the message because they qualify it rather than
          replace it, and above the audience because what is being sent is
          settled before who it goes to. */}
      <div className="space-y-s2">
        <AttachmentList
          files={attachments.files}
          onRemove={attachments.remove}
          channels={channels}
        />
        <AttachButtons
          onPick={attachments.add}
          disabled={attachments.isFull}
        />
      </div>

      {/* WHO IT IS FOR — "by user type per church group", in the client's
          own words. Three questions, revealed one at a time: choosing the
          whole church should not show two empty dropdowns. */}
      <div className="grid gap-s3 sm:grid-cols-2">
        <SelectField
          label="Send to"
          value={audience}
          onChange={(e) => {
            setAudience(e.target.value as AudienceKind)
            setGroupId('')
            setRoleId('')
          }}
          options={[
            ...(people ? [{ value: 'list', label: people.label }] : []),
            ...(bulk
              ? [
                  { value: 'church', label: 'Everyone in the church' },
                  { value: 'group', label: 'One group' },
                  { value: 'group_role', label: 'A role within a group' },
                  { value: 'filter', label: 'People who match rules…' },
                ]
              : []),
          ]}
        />

        {needsGroup || byRules ? (
          <SelectField
            label={byRules ? 'Within' : 'Group'}
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            options={[
              byRules
                ? { value: '', label: 'The whole church' }
                : { value: '', label: 'Choose a group' },
              ...(groups.data ?? []).map((g) => ({
                value: g.id,
                label: g.name,
              })),
            ]}
          />
        ) : null}

        {audience === 'group_role' ? (
          <SelectField
            label="Role"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            options={[
              { value: '', label: 'Choose a role' },
              ...(roles.data ?? []).map((r) => ({
                value: r.id,
                label: r.name,
              })),
            ]}
          />
        ) : null}
      </div>

      {byRules ? (
        <div className="space-y-s3 rounded border border-line-2 p-s3">
          <RuleBuilder churchId={churchId} value={rules} onChange={setRules} />
          <AudienceCount churchId={churchId} filter={rules} groupId={groupId || null} />
        </div>
      ) : null}

            {/* HOW IT REACHES THEM. Three toggles rather than a dropdown, because
          they combine — this is not a single choice. */}
      <div>
        <span className="mb-s2 block text-sm font-semibold text-ink-2">
          How it reaches them
        </span>
        <div className="flex flex-wrap gap-s2">
          <ChannelToggle
            icon={<Bell className="h-3.5 w-3.5" />}
            label="In the app"
            active={channels.in_app}
            onClick={() =>
              setChannels((c) => ({ ...c, in_app: !c.in_app }))
            }
          />
          <ChannelToggle
            icon={<Smartphone className="h-3.5 w-3.5" />}
            label="Push notification"
            active={channels.push}
            onClick={() => setChannels((c) => ({ ...c, push: !c.push }))}
          />
          <ChannelToggle
            icon={<MessageSquare className="h-3.5 w-3.5" />}
            label="SMS"
            active={channels.sms}
            /* OPENS ITSELF once an approved sentence exists to send.
               Texts need wording the carrier has registered, so until one
               template is approved this switch would lead to a screen with
               nothing choosable on it. Derived from the templates rather
               than hardcoded, so approval alone turns it on — no deploy. */
            disabled={!canSendTexts}
            title={
              canSendTexts
                ? undefined
                : 'Texts need approved wording before they can go out'
            }
            onClick={() => setChannels((c) => ({ ...c, sms: !c.sms }))}
          />
        </div>
      </div>

      {/* THE REGULATED PATH. A text is not free text — the carrier checks it
          against an approved sentence — so ticking SMS replaces writing with
          choosing. It appears only when that channel is on, which keeps the
          everyday message a single box. */}
      {regulated ? (
        <div className="space-y-s3 rounded border border-line-2 bg-surface-2 p-s3">
          <div>
            <h3 className="text-base font-bold">The text message</h3>
            <p className="text-sm text-ink-2">
              Texts have to use wording that has already been approved, so
              pick the one that fits and fill in what changes. Your church's
              name is added for you.
            </p>
          </div>
          <TemplatePicker
            churchId={churchId}
            channel="sms"
            onChange={setChoice}
          />
        </div>
      ) : null}

      {error ? (
        <p
          role="alert"
          className="flex items-start gap-s2 rounded-md bg-danger-soft px-s3 py-s2 text-base text-danger-ink"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {error}
        </p>
      ) : null}

      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={submit}
          disabled={!ready}
          loading={send.isPending}
        >
          <Send className="h-4 w-4" aria-hidden />
          Send
        </Button>
      </div>
    </Card>
  )
}

function ChannelToggle({
  icon,
  label,
  active,
  disabled,
  title,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  active: boolean
  disabled?: boolean
  title?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-s2 rounded-pill border px-s3 py-s2 text-sm font-semibold transition-colors',
        disabled
          ? 'cursor-default border-line-2 text-ink-3'
          : active
            ? 'border-brand bg-brand-soft text-brand'
            : 'border-line-2 text-ink-2 hover:bg-hover',
      )}
    >
      {icon}
      {label}
      {active && !disabled ? (
        <Check className="h-3.5 w-3.5" aria-hidden />
      ) : null}
    </button>
  )
}

/* ------------------------------------------------------------------- history */

function History({
  rows,
  live,
  loading,
}: {
  rows: ChurchMessage[]
  /* The one being sent right now, fresher than the list behind it. */
  live: ChurchMessage | null
  loading: boolean
}) {
  if (loading) {
    return (
      <Card className="p-s6 text-center text-ink-2">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
      </Card>
    )
  }

  if (!rows.length) {
    return (
      <Card className="p-s8 text-center">
        <p className="text-md font-bold">Nothing sent yet</p>
        <p className="mt-1 text-base text-ink-2">
          What you send your church will be listed here, with how many people
          received it.
        </p>
      </Card>
    )
  }

  return (
    <section className="space-y-s2">
      <h2 className="px-s1 text-md font-bold">Sent</h2>
      {rows.map((row) => (
        <MessageRow
          key={row.id}
          message={live && live.id === row.id ? live : row}
        />
      ))}
    </section>
  )
}

function MessageRow({ message }: { message: ChurchMessage }) {
  const sending = message.status === 'queued' || message.status === 'sending'
  const failed = message.status === 'failed'

  return (
    <Card className="p-s4">
      <div className="flex items-start justify-between gap-s3">
        <div className="min-w-0">
          {/* The title is the way into the report. A separate "view"
              link would be a second thing to aim at for the same place. */}
          <Link
            to={`/messages/${message.id}`}
            className="truncate font-semibold hover:text-brand hover:underline"
          >
            <h3 className="truncate">{message.title}</h3>
          </Link>
          <p className="mt-0.5 line-clamp-2 text-base text-ink-2">
            {message.body}
          </p>
        </div>
        <Status message={message} />
      </div>

      <AttachedFiles media={message.media ?? []} />

      <div className="mt-s3 flex flex-wrap items-center gap-x-s3 gap-y-1 text-sm text-ink-2">
        <span className="inline-flex items-center gap-1">
          <Users className="h-3.5 w-3.5" aria-hidden />
          {message.audience_label}
        </span>
        <span aria-hidden>·</span>
        <span>
          {/* PEOPLE, not channel rows — the server counts distinct people, so
              "3 of 3" cannot exceed the audience. */}
          {sending && !message.waiting_on
            ? `Sending to ${message.recipient_count}…`
            : `${message.delivered_count} of ${message.recipient_count} received`}
        </span>
        {/* WHAT IS STILL WAITING. A message with texts ticked has reached
            people in the app and on their phones while the SMS sits parked;
            "Sending…" forever would be a worse answer than naming it. */}
        {message.waiting_on ? (
          <>
            <span aria-hidden>·</span>
            <span className="text-warning-ink">{message.waiting_on}</span>
          </>
        ) : null}
        {message.failed_count > 0 ? (
          <>
            <span aria-hidden>·</span>
            <span className="text-warning-ink">
              {message.failed_count} could not be reached
            </span>
          </>
        ) : null}
        <span aria-hidden>·</span>
        <span>{timeAgo(message.sent_at ?? message.created_at)}</span>
        {message.author_name ? (
          <>
            <span aria-hidden>·</span>
            <span>{message.author_name}</span>
          </>
        ) : null}
      </div>

      {failed && message.error ? (
        <p className="mt-s2 rounded-md bg-danger-soft px-s3 py-s2 text-sm text-danger-ink">
          {message.error}
        </p>
      ) : null}
    </Card>
  )
}

function Status({ message }: { message: ChurchMessage }) {
  /* Waiting on a channel is not the same as still working. The spinner says
     "come back in a moment"; this says "the rest needs something we do not
     have yet", which is a different message and a different colour. */
  if (message.waiting_on) {
    return (
      <span className="shrink-0 rounded-pill bg-warning-soft px-s3 py-0.5 text-xs font-bold text-warning-ink">
        Partly sent
      </span>
    )
  }
  if (message.status === 'queued' || message.status === 'sending') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-pill bg-brand-soft px-s3 py-0.5 text-xs font-bold text-brand">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Sending
      </span>
    )
  }
  if (message.status === 'failed') {
    return (
      <span className="shrink-0 rounded-pill bg-danger-soft px-s3 py-0.5 text-xs font-bold text-danger-ink">
        Failed
      </span>
    )
  }
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-pill bg-success-soft px-s3 py-0.5 text-xs font-bold text-success">
      <Check className="h-3 w-3" aria-hidden />
      Sent
    </span>
  )
}
